import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, MobileStepper } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import type { FC } from "react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useSwipeGesture } from "../../../hooks/useSwipeGesture";
import { localStorageService } from "../../../services/LocalStorageService";
import { INK } from "../../../theme/tokens";
import type { TutorialStep } from "../../../utils/models";
import { shouldShowTutorial } from "./utils/utils";

type SystemTutorialProps = { tutorialKey: string; open: boolean; onClose: () => void; steps: TutorialStep[] };

export const SystemTutorial: FC<SystemTutorialProps> = ({ open, onClose, steps, tutorialKey }) => {
	const { t } = useTranslation();
	const {
		userInfo: { id: userId },
	} = useAuth();
	const titleId = useId();
	const [activeStep, setActiveStep] = useState<number>(0);
	const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);
	const touchStartX = useRef<number>(0);
	const touchEndX = useRef<number>(0);

	const currentStep = steps[activeStep] || {};

	const handleNext = (): void => {
		if (activeStep < steps.length - 1) setActiveStep((prev) => prev + 1);
		else handleClose();
	};

	const handleBack = (): void => setActiveStep((prev) => prev - 1);

	const handleClose = (): void => {
		if (dontShowAgain) localStorageService.setTutorialDismissed(tutorialKey, true, userId);
		setActiveStep(0);
		setDontShowAgain(false);
		onClose();
	};

	const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipeGesture(touchStartX, touchEndX, { onSwipeLeft: handleNext, onSwipeRight: handleBack });

	if (!shouldShowTutorial(tutorialKey, userId)) return null;

	return (
		<Dialog open={open} onClose={handleClose} aria-labelledby={titleId}>
			<DialogTitle color={INK.SECONDARY} id={titleId}>
				{currentStep.title || ""}
			</DialogTitle>
			<DialogContent onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
				<AnimatePresence mode="wait">
					<motion.div key={activeStep} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
							}}
						>
							{currentStep.image && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<img src={currentStep.image} alt={currentStep.title || `Step ${activeStep + 1}`} style={{ maxWidth: "100%", maxHeight: "300px" }} />
								</Box>
							)}
							{currentStep.body && <DialogContentText>{currentStep.body}</DialogContentText>}
							<MobileStepper
								variant="dots"
								steps={steps.length}
								position="static"
								activeStep={activeStep}
								sx={{ flexGrow: 1, bgcolor: "transparent", p: 0 }}
								nextButton={
									<Button size="small" onClick={handleNext}>
										{activeStep === steps.length - 1 ? t("OK") : t("Next")}
									</Button>
								}
								backButton={
									<Button size="small" onClick={handleBack} disabled={activeStep === 0}>
										{t("Back")}
									</Button>
								}
							/>
						</Box>
					</motion.div>
				</AnimatePresence>
			</DialogContent>
			<DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
				<FormControlLabel
					control={<Checkbox checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} size="small" />}
					label={t("dont.show.again")}
				/>
			</DialogActions>
		</Dialog>
	);
};
